/**
 * The sites in the rail: the shortest way back to the thing you were doing.
 *
 * Two variants, desktop and mobile, each with its own set of Tailwind classes
 * for the same row: a different size, a different padding, a different icon
 * scale. There is one row now. A phone shows the same list at the same size as
 * a laptop, because it is the same list, and a name that was worth reading on
 * one screen does not become 12px on the other.
 */
import { useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Plus, ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/hooks/useTeam'
import { apiService } from '@/services/api'
import { SideGroup, SideLink } from '@/design-system/app'
import { Skeleton } from '@/design-system/primitives'
import DomainFavicon from './DomainFavicon'
import DomainLimitModal from './DomainLimitModal'
import type { UnifiedDomain } from '@shared/types'

const COLLAPSED_COUNT = 5
const EXPANDED_COUNT = 10

interface SidebarDomainListProps {
  domains: UnifiedDomain[]
  isLoading: boolean
  hasDomains: boolean
  onMobileClose?: () => void
  variant: 'desktop' | 'mobile'
  /**
   * Open the add-a-site dialog. The dialog itself is mounted by Layout, not
   * here, because on a phone this list lives inside the drawer: closing the
   * drawer unmounts the list, and it would take the dialog down with it the
   * moment it opened. Layout outlives both.
   */
  onAddSite: () => void
}

const pathFor = (domain: UnifiedDomain) => `/domains/${encodeURIComponent(domain.domain)}`
const nameFor = (domain: UnifiedDomain) => domain.displayName || domain.domain

/** One site, wherever it appears: the rail, the drawer, or under the pointer. */
function SiteRow({
  domain,
  isActive,
  onGo,
  grip,
}: {
  domain: UnifiedDomain
  isActive?: boolean
  onGo?: () => void
  grip?: boolean
}) {
  return (
    <SideLink
      to={pathFor(domain)}
      current={isActive}
      onClick={onGo}
      title={nameFor(domain)}
      className="ds-rail__site"
      icon={<DomainFavicon domain={domain.domain} />}
      after={grip ? <GripVertical className="ds-rail__grip" aria-hidden="true" /> : undefined}
    >
      {nameFor(domain)}
    </SideLink>
  )
}

// ─── Sortable Domain Item ────────────────────────────────────────────────────

function SortableDomainItem({
  domain,
  isActive,
  onMobileClose,
}: {
  domain: UnifiedDomain
  isActive: boolean
  onMobileClose?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: domain.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <SiteRow domain={domain} isActive={isActive} onGo={onMobileClose} grip />
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SidebarDomainList({
  domains,
  isLoading,
  hasDomains,
  onMobileClose,
  variant,
  onAddSite,
}: SidebarDomainListProps) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { currentTeam, currentUserRole } = useTeam()
  const teamId = currentTeam?.team?.id || 'personal'
  const activeTeamId = currentTeam?.team?.id
  const canManageDomainRegistry = currentUserRole === 'owner' || currentUserRole === 'admin'
  const isMobile = variant === 'mobile'
  const [showDomainLimitModal, setShowDomainLimitModal] = useState(false)

  const { data: domainLimitsData } = useQuery({
    queryKey: ['domain-limits', activeTeamId],
    queryFn: () => apiService.getDomainLimits(),
    enabled: !!activeTeamId,
  })

  const domainLimits = domainLimitsData?.data
  const isDomainLimitReached = domainLimits?.allowed === false

  // Check if domains query has ever returned data (prevents flash of empty state)
  const hasFetched = queryClient.getQueryState(['unified-domains', currentTeam?.team?.id || null])?.dataUpdatedAt !== undefined
    && (queryClient.getQueryState(['unified-domains', currentTeam?.team?.id || null])?.dataUpdatedAt ?? 0) > 0

  // Expand/collapse state persisted per team in localStorage
  const storageKey = `superschema_domains_sidebar_expanded_${teamId}`
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true'
    } catch {
      return false
    }
  })

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => {
      const next = !prev
      try { localStorage.setItem(storageKey, String(next)) } catch { /* noop */ }
      return next
    })
  }, [storageKey])

  // Drag state
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const activeDragDomain = activeDragId ? domains.find(d => d.id === activeDragId) : null

  // Sensors: require 5px movement to distinguish click from drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Determine visible domains
  const maxVisible = isExpanded ? EXPANDED_COUNT : COLLAPSED_COUNT
  const visibleDomains = domains.slice(0, maxVisible)
  const hasMore = domains.length > maxVisible
  const canExpand = domains.length > COLLAPSED_COUNT

  // Drag-and-drop is for reordering the rail, which a phone cannot do anyway.
  const canReorder = !isMobile && canManageDomainRegistry

  // ─── Drag Handlers ───────────────────────────────────────────────────

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
  }, [])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = domains.findIndex(d => d.id === active.id)
    const newIndex = domains.findIndex(d => d.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Compute new order
    const reordered = arrayMove(domains, oldIndex, newIndex)

    // Build orderings for all domains (assign sequential display_order)
    const orderings = reordered.map((d, i) => ({ id: d.id, displayOrder: i }))

    // Optimistic update
    const queryKey = ['unified-domains', currentTeam?.team?.id || null]
    const prevData = queryClient.getQueryData(queryKey)
    queryClient.setQueryData(queryKey, reordered.map((d, i) => ({ ...d, displayOrder: i })))

    try {
      await apiService.reorderDomains(orderings)
    } catch (err) {
      console.error('Failed to reorder domains:', err)
      // Rollback
      queryClient.setQueryData(queryKey, prevData)
    }
  }, [domains, currentTeam?.team?.id, queryClient])

  /*
    The button says "Add a site", so it adds a site. It used to navigate to the
    dashboard and leave you to find the button there, which is a different
    promise from the one the label makes.

    The limit is checked here rather than left to the server so that somebody
    with no room does not type an address before being told. AddDomainDialog
    catches it a second time on the response, for the case where the limit is
    reached in another tab between this check and the submit.
  */
  const handleAddDomainClick = useCallback(() => {
    if (isDomainLimitReached) {
      setShowDomainLimitModal(true)
      return
    }

    onMobileClose?.()
    onAddSite()
  }, [isDomainLimitReached, onAddSite, onMobileClose])

  // ─── Render ──────────────────────────────────────────────────────────

  const domainItems = visibleDomains.map(domain => {
    const isActive = location.pathname.startsWith(pathFor(domain))

    if (!canReorder) {
      return <SiteRow key={domain.id} domain={domain} isActive={isActive} onGo={onMobileClose} />
    }

    return (
      <SortableDomainItem
        key={domain.id}
        domain={domain}
        isActive={isActive}
        onMobileClose={onMobileClose}
      />
    )
  })

  return (
    <>
      <div className="ds-rail__rule" />
      {/*
        "Your sites" rather than "Your Domains": the card this list leads to
        is headed "Your sites", and the rail should call a thing what the page
        it opens calls it.
      */}
      <SideGroup>Your sites</SideGroup>

      {/* While the list is on its way, hold its shape rather than collapse. */}
      {(isLoading || (!hasDomains && !hasFetched)) && (
        <div className="ds-rail__wait" data-testid="sidebar-domains-waiting">
          <Skeleton height={22} />
          <Skeleton height={22} width="72%" />
        </div>
      )}

      {/* The list */}
      {!isLoading && hasDomains && (
        <>
          {canReorder ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={visibleDomains.map(d => d.id)}
                strategy={verticalListSortingStrategy}
              >
                {domainItems}
              </SortableContext>
              <DragOverlay>
                {activeDragDomain && <SiteRow domain={activeDragDomain} />}
              </DragOverlay>
            </DndContext>
          ) : (
            <>{domainItems}</>
          )}

          {canExpand && !isExpanded && (
            <SideLink
              className="ds-rail__more"
              onClick={toggleExpanded}
              icon={<ChevronDown aria-hidden="true" />}
            >
              Show {Math.min(domains.length - COLLAPSED_COUNT, EXPANDED_COUNT - COLLAPSED_COUNT)} more
            </SideLink>
          )}

          {isExpanded && hasMore && (
            <SideLink
              className="ds-rail__more"
              to="/dashboard"
              onClick={onMobileClose}
              icon={<Plus aria-hidden="true" />}
            >
              {domains.length - EXPANDED_COUNT} more on the dashboard
            </SideLink>
          )}

          {isExpanded && !hasMore && canExpand && (
            <SideLink
              className="ds-rail__more"
              onClick={toggleExpanded}
              icon={<ChevronUp aria-hidden="true" />}
            >
              Show less
            </SideLink>
          )}
        </>
      )}

      {/*
        Add sits under the list, with the other things that act on the list.
        Above the first site it read as a site. It is here once the list has
        settled whether or not there is one, which is when there is nothing
        else in this group at all.
      */}
      {!isLoading && hasFetched && canManageDomainRegistry && (
        <SideLink
          className="ds-rail__more"
          onClick={handleAddDomainClick}
          icon={<Plus aria-hidden="true" />}
        >
          {hasDomains ? 'Add a site' : 'Add your first site'}
        </SideLink>
      )}

      <DomainLimitModal
        isOpen={showDomainLimitModal}
        onClose={() => setShowDomainLimitModal(false)}
        currentCount={domainLimits?.current ?? domains.length}
        maxCount={domainLimits?.max ?? domains.length}
        reason="add another site"
        triggerSource="SidebarDomainList"
      />
    </>
  )
}
